import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { structureApi, type PositionDetail, type PositionNode } from "../api/structure";
import type { AssetDetail, DismountAllResult } from "../api/assets";
import type { ApiError } from "../api/client";
import type { TemplateDetail } from "../api/templates";
import { layoutsApi } from "../api/layouts";
import { detailsMap, formatApiError } from "../lib/apiError";
import { useAuth } from "../auth/AuthContext";
import { useToast } from "../components/Toast";
import { useConfirm, usePrompt } from "../components/ConfirmDialog";
import { TreeView } from "../components/TreeView";
import { SplitHandle, useSplitWidth } from "../components/SplitHandle";
import { PositionKindIcon, PositionKindName } from "../components/PositionKindIcon";
import { usePositionKinds } from "../lib/usePositionKinds";
import { useStructureScope } from "../lib/structureScope";
import { ancestorIds } from "../lib/positionTree";
import { AttachmentsPanel } from "../components/AttachmentsPanel";
import { AssetEventsTable } from "../components/AssetEventsTable";
import { CommentsPanel } from "../components/CommentsPanel";
import { HistoryPanel } from "../components/HistoryPanel";
import { MountModal } from "../components/MountModal";
import { DismountModal } from "../components/DismountModal";
import { MOUNT_STATE_PARAM, PositionsTable } from "../components/PositionsTable";
import { ContextMenu, type ContextMenuItem } from "../components/ContextMenu";
import { NewLayoutModal } from "../components/layout/NewLayoutModal";
import Ic from "../components/icons.jsx";
import { MountDot, SlotCounter, dangerBtn, errorBox, positionTitle, translateError, type ModalState } from "./structure/shared";
import { usePositionTree } from "./structure/usePositionTree";
import { PositionFormModal } from "./structure/PositionFormModal";
import { MoveModal } from "./structure/MoveModal";
import { CopyModal } from "./structure/CopyModal";
import { ApplyTemplateModal, SaveTemplateModal } from "./structure/TemplateModals";
import { ReferenceValuesSection } from "./structure/ReferenceValuesSection";
import { BomSection } from "./structure/BomSection";
import { PositionLayoutsSection } from "./structure/PositionLayoutsSection";
import { DeletedBanner, LocationSection, MountedAssetSection, NotesSection } from "./structure/PositionInfoSections";

/** Left side of the page: the lazy tree, or a flat filterable table of every position. */
type ViewMode = "tree" | "table";
const VIEW_MODE_KEY = "bkm.structureView";
/** "0" = the "2/13" counters are switched off in the tree options (per browser, 2026-10-15). */
const SLOT_COUNTS_KEY = "bkm.structureSlotCounts";
// Raširi sve + Skupi sve + "⋯" on one row (tree-tools inner width, px).
const TREE_TOOLS_MIN_WIDTH = 260;

/**
 * `/struktura` and `/struktura/:id`. The URL is the single source of truth
 * for the selection; the left tree is loaded lazily (depth 2 up front, one
 * level per expand) and the right side shows the position card.
 *
 * `standalone` (`/pozicija/:id`): only the card, no tree, no table — what "Otvori u novom
 * ekranu" opens in a second browser tab so the tree stays where it was in the first one.
 *
 * Tree state lives in `structure/usePositionTree`, card sections and modals in `structure/*`;
 * this component wires them together and owns the actions that touch both sides.
 */
export default function StructurePage({ standalone = false }: { standalone?: boolean } = {}) {
  const { id: routeId } = useParams<{ id: string }>();
  const selectedId = routeId ?? null;
  const navigate = useNavigate();
  const { t } = useTranslation();
  const split = useSplitWidth("bkm.split.structure");
  const toast = useToast();
  const confirm = useConfirm();
  const prompt = usePrompt();
  const { hasPermission } = useAuth();
  // Sprint 7: one function per action (structure:create / edit / delete, layouts:create, templates:create).
  const canCreate = hasPermission("structure:create");
  const canEdit = hasPermission("structure:edit");
  const canDelete = hasPermission("structure:delete");
  const canCreateLayout = hasPermission("layouts:create");
  const canCreateTemplate = hasPermission("templates:create");
  // Sprint 5j: the CAD import is back on the screen (2026-10-20), still only for structure:import.
  const canImport = hasPermission("structure:import");
  const canMount = hasPermission("assets:mount");
  // Sastavnica of the position (sprint 5l): the section hides itself without bom:view.
  const canCreateBom = hasPermission("bom:create");
  const canEditBom = hasPermission("bom:edit");
  const canDeleteBom = hasPermission("bom:delete");
  // Neighbouring modules drawn on the position card (audit 2026-09-23 §C): shown only with their view.
  const canViewAssets = hasPermission("assets:view");
  const canViewLayouts = hasPermission("layouts:view");
  const canViewTemplates = hasPermission("templates:view");
  // Kinds that do not hold an asset are structural nodes: no expected type, no mount (2026-09-11).
  const { holdsAssetOf } = usePositionKinds();
  // Sprint 7 §12: with a structure scope, the structure and sastavnica functions work only inside its branches.
  const scope = useStructureScope();

  // ---- view mode (remembered per browser) ---------------------------------
  // A link with `?mountState=` (dashboard tile) lands on the table, where that filter lives.
  const [initialTable] = useState(() => {
    if (new URLSearchParams(window.location.search).has(MOUNT_STATE_PARAM)) return true;
    try { return localStorage.getItem(VIEW_MODE_KEY) === "table"; } catch { return false; }
  });
  const [viewMode, setViewModeState] = useState<ViewMode>(initialTable ? "table" : "tree");
  // Once opened, the table stays mounted (hidden) so switching back and forth is instant.
  const [tableMounted, setTableMounted] = useState(initialTable);
  const setViewMode = useCallback((mode: ViewMode) => {
    setViewModeState(mode);
    if (mode === "table") setTableMounted(true);
    try { localStorage.setItem(VIEW_MODE_KEY, mode); } catch { /* ignore */ }
  }, []);

  // ---- tree -------------------------------------------------------------
  const {
    roots, treeError, expanded, setExpanded, expandIds, treeRoot, setTreeRoot, showDeleted, setShowDeleted,
    expandingAll, expandAll, collapseAll, onExpand, refreshChildren, siblingsOf, revealPath,
  } = usePositionTree(!standalone);

  const select = useCallback((id: string | null) => {
    if (standalone && id) { navigate(`/pozicija/${id}`); return; }
    navigate(id ? `/struktura/${id}` : "/struktura");
  }, [standalone, navigate]);

  // ---- detail -----------------------------------------------------------
  const [detail, setDetail] = useState<PositionDetail | null>(null);
  /** True while another position's card is being fetched — the old card stays (dimmed) so the page keeps its height. */
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState<string | null>(null);
  const [modal, setModal] = useState<ModalState | null>(null);
  const [eventsKey, setEventsKey] = useState(0);
  // Bumped after a write on the card that the position's history lists (sastavnica lines).
  const [historyKey, setHistoryKey] = useState(0);

  const detailInScope = !!detail && scope.inScope([...detail.path.map((p) => p.id), detail.id]);
  // Holds a structure or sastavnica function, but not for this position: the card says why its buttons are missing.
  const outOfScopeNote = !!detail && !detailInScope
    && (canCreate || canEdit || canDelete || canCreateBom || canEditBom || canDeleteBom);

  const loadDetail = useCallback(async (id: string) => {
    setDetailError(null);
    try {
      setDetail(await structureApi.get(id));
    } catch (err) {
      setDetailError((err as ApiError).message);
    }
  }, []);

  useEffect(() => {
    setDetailError(null);
    if (!selectedId) { setDetail(null); return; }
    // Keep the previous card on screen until the new one arrives: emptying it would
    // shrink the page and yank the scroll position when the user is scrolled down.
    let cancelled = false;
    setDetailLoading(true);
    loadDetail(selectedId).finally(() => { if (!cancelled) setDetailLoading(false); });
    return () => { cancelled = true; };
  }, [selectedId, loadDetail]);

  // Deep link / after move: make sure every ancestor is loaded and open so the
  // selected row is visible (contract §5 — `path` runs root → parent).
  const treeReady = roots !== null;
  useEffect(() => {
    if (!detail || !treeReady) return;
    let cancelled = false;
    revealPath(detail.path, () => cancelled).catch((err: ApiError) => { if (!cancelled) toast.error(err.message); });
    return () => { cancelled = true; };
  }, [detail, treeReady, revealPath, toast]);

  /** "Demontiraj ceo sklop": the modal says how many assets sit directly in the assembly — counted from the direct children. */
  async function openDismountAll(position: PositionDetail) {
    try {
      const page = await structureApi.list({ parentId: position.id, size: 100, sort: "sortOrder,asc" });
      const count = page.content.filter((p) => p.mountedAssetId !== null).length;
      setModal({ kind: "dismountAll", position, count });
    } catch (err) {
      toast.error((err as ApiError).message);
    }
  }

  /** Tree menu "Prikaži na layoutu": checked after the card loads (contract §5) — a toast when nobody in the chain is marked. */
  async function showOnLayout(node: PositionNode) {
    try {
      const d = await structureApi.get(node.id);
      if (d.placementCount === 0) { toast.info(t("layout.notPlaced", "Pozicija nije na layoutu.")); return; }
      const p = (await layoutsApi.placements(d.id))[0];
      if (!p) { toast.info(t("layout.notPlaced", "Pozicija nije na layoutu.")); return; }
      navigate(`/layout/${p.layoutId}?marker=${p.distance === 0 ? d.id : p.placedPositionId}`);
    } catch (err) {
      toast.error((err as ApiError).message);
    }
  }

  // ---- actions ----------------------------------------------------------
  async function handleCreated(parent: PositionDetail | null, created: PositionDetail[]) {
    setModal(null);
    toast.success(created.length > 1
      ? t("structure.toast.createdMany", "Kreirano {{count}} pozicija.", { count: created.length })
      : t("structure.toast.created", "Pozicija kreirana."));
    await refreshChildren(parent?.id ?? null);
    if (parent) expandIds([parent.id]);
    if (created[0]) select(created[0].id);
  }

  /** After `apply-template`: reload the branch and open the first created root. */
  async function handleTemplateApplied(parent: PositionDetail, created: PositionDetail[]) {
    setModal(null);
    toast.success(t("structure.toast.templateApplied", "Iz šablona kreirano {{count}} pozicija.", { count: created.length }));
    await refreshChildren(parent.id);
    expandIds([parent.id]);
    if (created[0]) select(created[0].id);
    else await loadDetail(parent.id);
  }

  async function handleTemplateSaved(saved: TemplateDetail) {
    setModal(null);
    toast.success(t("structure.toast.templateSaved", "Šablon „{{name}}“ sačuvan ({{count}} čvorova).", { name: saved.name, count: saved.nodeCount }));
  }

  async function handleEdited(saved: PositionDetail) {
    setModal(null);
    toast.success(t("structure.toast.saved", "Pozicija sačuvana."));
    setDetail(saved);
    await refreshChildren(saved.parentId);
  }

  async function handleMoved(saved: PositionDetail, oldParentId: string | null) {
    setModal(null);
    toast.success(t("structure.toast.moved", "Pozicija premeštena."));
    await refreshChildren(oldParentId);
    await refreshChildren(saved.parentId);
    if (saved.parentId) expandIds([saved.parentId]);
    setDetail(saved);
  }

  async function handleCopied(targetParentId: string | null, copies: PositionDetail[]) {
    setModal(null);
    toast.success(copies.length > 1
      ? t("structure.toast.copiedMany", "Napravljeno {{count}} kopija grane.", { count: copies.length })
      : t("structure.toast.copied", "Grana kopirana."));
    await refreshChildren(targetParentId);
    if (targetParentId) expandIds([targetParentId]);
    if (copies[0]) select(copies[0].id);
  }

  /**
   * "Vrati" on a deleted card: undo the soft delete, then refresh the card and its parent's level.
   * When an active position took the code in the meantime, ask for a new code (or none) and retry.
   */
  async function handleRestore(code?: string, dropChildCodes = false, confirmed = false) {
    if (!detail?.deletedAt) return;
    if (!confirmed) {
      const ok = await confirm({
        title: t("structure.confirmRestore.title", "Vraćanje pozicije"),
        message: detail.descendantCount > 0
          ? t("structure.confirmRestore.messageBranch", "Vratiti poziciju „{{name}}“ i pod-pozicije obrisane zajedno sa njom?", { name: positionTitle(detail) })
          : t("structure.confirmRestore.message", "Vratiti poziciju „{{name}}“?", { name: positionTitle(detail) }),
        confirmLabel: t("structure.restore", "Vrati"),
      });
      if (!ok) return;
    }
    try {
      const restored = await structureApi.restore(detail.id, code, dropChildCodes);
      setDetail(restored);
      toast.success(t("structure.toast.restored", "Pozicija vraćena."));
      await refreshChildren(restored.parentId);
    } catch (err) {
      const ae = err as ApiError;
      if (ae.code === "PARENT_DELETED") { toast.error(t("structure.error.parentDeleted", "Prvo vratite nadređenu poziciju.")); return; }
      if (ae.code === "CODE_TAKEN") {
        const d = detailsMap(ae);
        const taken = String(d?.code ?? code ?? detail.code ?? "");
        const holder = d?.heldBy ? String(d.heldBy) : null;
        const next = await prompt({
          title: t("structure.restoreCode.title", "Šifra je zauzeta"),
          message: holder
            ? t("structure.restoreCode.messageHolder", "Šifru „{{code}}“ sada ima pozicija „{{holder}}“. Unesite novu šifru za vraćenu poziciju, ili ostavite prazno da se vrati bez šifre.", { code: taken, holder })
            : t("structure.restoreCode.message", "Šifra „{{code}}“ je zauzeta. Unesite novu šifru za vraćenu poziciju, ili ostavite prazno da se vrati bez šifre.", { code: taken }),
          placeholder: t("structure.restoreCode.placeholder", "Nova šifra (prazno = bez šifre)"),
          confirmLabel: t("structure.restore", "Vrati"),
        });
        if (next === null) return;
        await handleRestore(next.trim(), dropChildCodes, true);
        return;
      }
      if (ae.code === "CHILD_CODE_TAKEN") {
        const codes = (detailsMap(ae)?.codes as string[] | undefined) ?? [];
        const ok = await confirm({
          title: t("structure.restoreChildCodes.title", "Šifre pod-pozicija su zauzete"),
          message: t("structure.restoreChildCodes.message", "Aktivne pozicije sada drže šifre: {{codes}}. Vratiti te pod-pozicije bez šifre (šifre posle možete izmeniti na kartonu)?", { codes: codes.join(", ") }),
          confirmLabel: t("structure.restoreChildCodes.confirm", "Vrati bez šifre"),
        });
        if (!ok) return;
        await handleRestore(code, true, true);
        return;
      }
      toast.error(formatApiError(ae));
    }
  }

  /** Deletes `target` (the open card by default; the right-click menu passes another node's detail). */
  async function handleDelete(target: PositionDetail | null = detail) {
    if (!target) return;
    const reason = await prompt({
      title: t("structure.confirmDelete.title", "Brisanje pozicije"),
      message: t("structure.confirmDelete.message", "Obrisati poziciju „{{name}}“?", { name: positionTitle(target) }),
      confirmLabel: t("action.delete", "Obriši"),
      placeholder: t("deleteReason.placeholder", "Razlog brisanja (obavezno) — npr. rashodovano, zamenjeno, greška pri unosu…"),
      multiline: true,
      required: true,
      danger: true,
    });
    if (!reason) return;
    try {
      try {
        await structureApi.remove(target.id, false, reason);
      } catch (err) {
        const ae = err as ApiError;
        if (ae.code === "HAS_MOUNTED_ASSETS") {
          const n = Number(detailsMap(ae)?.count ?? 0);
          toast.error(t("structure.error.hasMountedAssets", "U grani ima montiranih asseta ({{count}}) — prvo ih demontirajte.", { count: n }));
          return;
        }
        if (ae.code !== "HAS_CHILDREN") throw ae;
        const count = Number(detailsMap(ae)?.childCount ?? target.childCount) || target.childCount;
        const cascade = await confirm({
          title: t("structure.confirmCascade.title", "Pozicija ima pod-pozicije"),
          message: t("structure.confirmCascade.message", "Obrisati „{{name}}“ i {{count}} pod-pozicija?", { name: positionTitle(target), count }),
          confirmLabel: t("structure.confirmCascade.confirm", "Obriši sve"),
          danger: true,
        });
        if (!cascade) return;
        await structureApi.remove(target.id, true, reason);
      }
      toast.success(t("structure.toast.deleted", "Pozicija obrisana."));
      const parentId = target.parentId;
      if (treeRoot?.id === target.id) setTreeRoot(null);
      if (selectedId === target.id || (selectedId && detail?.path.some((p) => p.id === target.id))) select(parentId);
      await refreshChildren(parentId);
    } catch (err) {
      toast.error((err as ApiError).message);
    }
  }

  /** After a mount/dismount: refresh the card, the parent's rows (badges) and the history. */
  async function afterMountChange(message: string) {
    setModal(null);
    toast.success(message);
    setEventsKey((k) => k + 1);
    if (!detail) return;
    await loadDetail(detail.id);
    await refreshChildren(detail.parentId);
  }

  async function handleMounted(_d: AssetDetail) {
    await afterMountChange(t("structure.toast.mounted", "Asset montiran."));
  }

  async function handleDismounted(_d: AssetDetail) {
    await afterMountChange(t("structure.toast.dismounted", "Asset demontiran."));
  }

  async function handleDismountedAll(res: DismountAllResult) {
    await afterMountChange(t("structure.toast.dismountedAll", "Demontirano {{count}} asseta.", { count: res.count }));
    // Badges deeper in the branch changed too — reload what is open below the position.
    if (detail) await refreshChildren(detail.id);
  }

  const loadPositionEvents = useCallback(
    (page: number, size: number) => structureApi.events(selectedId ?? "", { page, size }),
    [selectedId],
  );

  // ---- tree accessors ---------------------------------------------------
  const getId = useCallback((n: PositionNode) => n.id, []);
  const getChildren = useCallback((n: PositionNode) => n.children, []);
  const getHasChildren = useCallback((n: PositionNode) => n.hasChildren, []);
  const getLabel = useCallback(
    (n: PositionNode) => (
      <span className={n.deletedAt ? "tree-deleted" : undefined}>
        {n.name}
        {n.sequenceNo !== null && <span className="muted"> #{n.sequenceNo}</span>}
      </span>
    ),
    [],
  );
  // A slot shows its own dot, a node without an asset of its own the "2/13" of its branch —
  // unless the counters are switched off in the tree options.
  const [showSlotCounts, setShowSlotCounts] = useState<boolean>(() => {
    try { return localStorage.getItem(SLOT_COUNTS_KEY) !== "0"; } catch { return true; }
  });
  useEffect(() => {
    try { localStorage.setItem(SLOT_COUNTS_KEY, showSlotCounts ? "1" : "0"); } catch { /* ignore */ }
  }, [showSlotCounts]);
  const renderBadge = useCallback(
    (n: PositionNode) => <>{showSlotCounts && <SlotCounter node={n} />}<MountDot node={n} /></>,
    [showSlotCounts],
  );

  // ---- right-click menu on tree rows -----------------------------------------
  const [ctx, setCtx] = useState<{ x: number; y: number; node: PositionNode } | null>(null);
  // Secondary card actions (move / copy / templates) live under a "⋯" button (Strahinja, 2026-09-11).
  const [moreMenu, setMoreMenu] = useState<{ x: number; y: number } | null>(null);
  const [treeMenu, setTreeMenu] = useState<{ x: number; y: number } | null>(null);
  // When the tree pane is too narrow for "Raširi sve" + "Skupi sve" + "⋯" on one row, the two
  // buttons move into the "⋯" menu instead of wrapping onto a second row.
  const treeToolsRef = useRef<HTMLDivElement>(null);
  const [treeToolsCompact, setTreeToolsCompact] = useState(false);
  useLayoutEffect(() => {
    const el = treeToolsRef.current;
    if (!el || typeof ResizeObserver === "undefined") return;
    const measure = () => setTreeToolsCompact(el.clientWidth < TREE_TOOLS_MIN_WIDTH);
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [standalone, viewMode]);
  const closeCtx = useCallback(() => setCtx(null), []);
  /** Ids from the top of the structure down to a loaded tree node (a focused branch adds its own chain above the forest). */
  const chainOf = (nodeId: string): string[] =>
    [...(treeRoot?.chain ?? []), ...(ancestorIds(roots ?? [], nodeId) ?? []), nodeId];
  const ctxChain = ctx ? chainOf(ctx.node.id) : [];
  const ctxInScope = !!ctx && scope.inScope(ctxChain);
  // Reordering renumbers every sibling, so the parent's whole level must be in scope.
  const ctxSiblingsInScope = !!ctx && scope.inScope(ctxChain.slice(0, -1));
  const onRowContextMenu = useCallback((node: PositionNode, point: { x: number; y: number }) => {
    setCtx({ x: point.x, y: point.y, node });
  }, []);

  /**
   * Swaps the node with its previous / next sibling and renumbers the whole
   * sibling list (`sortOrder` = index) through `move` with the same parent, so
   * the order is explicit and stable regardless of names.
   */
  async function reorder(node: PositionNode, dir: -1 | 1) {
    const siblings = siblingsOf(node);
    const i = siblings.findIndex((n) => n.id === node.id);
    const j = i + dir;
    if (i < 0 || j < 0 || j >= siblings.length) return;
    const next = [...siblings];
    [next[i], next[j]] = [next[j], next[i]];
    try {
      for (let k = 0; k < next.length; k++) {
        if (next[k].sortOrder !== k) await structureApi.move(next[k].id, { parentId: node.parentId, sortOrder: k });
      }
      await refreshChildren(node.parentId);
      if (detail && siblings.some((n) => n.id === detail.id)) await loadDetail(detail.id);
    } catch (err) {
      toast.error(translateError(t, err as ApiError));
    }
  }

  /** Every menu action works on the node's full detail (the modals need it) and opens its card. */
  async function withDetail(node: PositionNode, run: (d: PositionDetail) => void) {
    try {
      const d = await structureApi.get(node.id);
      select(node.id);
      run(d);
    } catch (err) {
      toast.error((err as ApiError).message);
    }
  }

  /** The card alone, in a second tab; the tree in this tab is left untouched. */
  function openInNewScreen(id: string) {
    window.open(`/pozicija/${id}`, "_blank", "noopener");
  }

  const ctxItems: ContextMenuItem[] = ctx ? [
    ...(canCreate && ctxInScope ? [
      { label: t("structure.addChild", "Dodaj pod-poziciju"), icon: <Ic.plus />, onSelect: () => withDetail(ctx.node, (d) => setModal({ kind: "create", parent: d })) },
    ] : []),
    ...(canEdit && ctxInScope ? [
      { label: t("action.edit", "Izmeni"), icon: <Ic.cog />, onSelect: () => withDetail(ctx.node, (d) => setModal({ kind: "edit", position: d })) },
      { label: t("structure.move", "Premesti"), icon: <Ic.arrowRight />, onSelect: () => withDetail(ctx.node, (d) => setModal({ kind: "move", position: d })) },
      ...(() => {
        if (!ctxSiblingsInScope) return [];
        const sibs = siblingsOf(ctx.node);
        const i = sibs.findIndex((n) => n.id === ctx.node.id);
        return [
          ...(i > 0 ? [{ label: t("structure.moveUp", "Pomeri gore"), icon: <Ic.arrowUp />, onSelect: () => { void reorder(ctx.node, -1); } }] : []),
          ...(i >= 0 && i < sibs.length - 1 ? [{ label: t("structure.moveDown", "Pomeri dole"), icon: <Ic.arrowDown />, onSelect: () => { void reorder(ctx.node, 1); } }] : []),
        ];
      })(),
    ] : []),
    ...(canCreate ? [
      { label: t("structure.copy", "Kopiraj granu"), icon: <Ic.copy />, onSelect: () => withDetail(ctx.node, (d) => setModal({ kind: "copy", position: d })) },
    ] : []),
    ...(canCreate && canViewTemplates && ctxInScope ? [
      { label: t("structure.addFromTemplate", "Dodaj iz šablona"), icon: <Ic.layers />, onSelect: () => withDetail(ctx.node, (d) => setModal({ kind: "applyTemplate", position: d })) },
    ] : []),
    ...(canMount && !ctx.node.mountedAssetId && holdsAssetOf(ctx.node.kind) ? [
      { label: t("structure.mountAsset", "Montiraj asset"), icon: <Ic.box />, separator: canCreate || (canEdit && ctxInScope), onSelect: () => withDetail(ctx.node, (d) => setModal({ kind: "mount", position: d })) },
    ] : []),
    ...(ctx.node.hasChildren && treeRoot?.id !== ctx.node.id ? [
      { label: t("structure.focus.here", "Fokus na granu"), icon: <Ic.tree />, separator: true, onSelect: () => setTreeRoot({ id: ctx.node.id, name: positionTitle(ctx.node), chain: ctxChain }) },
    ] : []),
    { label: t("structure.openInNewScreen", "Otvori u novom ekranu"), icon: <Ic.external />, separator: !(ctx.node.hasChildren && treeRoot?.id !== ctx.node.id), onSelect: () => openInNewScreen(ctx.node.id) },
    ...(canViewLayouts ? [
      { label: t("layout.showOnLayout", "Prikaži na layoutu"), icon: <Ic.layout />, separator: true, onSelect: () => { void showOnLayout(ctx.node); } },
    ] : []),
    ...(canCreateLayout ? [
      { label: t("layout.new.title", "Novi layout"), icon: <Ic.image />, onSelect: () => withDetail(ctx.node, (d) => setModal({ kind: "newLayout", position: d })) },
    ] : []),
    ...(canDelete && ctxInScope ? [
      { label: t("action.delete", "Obriši"), icon: <Ic.x />, danger: true, separator: true, onSelect: () => withDetail(ctx.node, (d) => { void handleDelete(d); }) },
    ] : []),
  ] : [];


  return (
    <div>
      <div className="page-head">
        <div>
          <h1>{standalone ? t("structure.standalone.title", "Karton pozicije") : t("structure.title", "Struktura")}</h1>
          <div className="sub">{standalone
            ? t("structure.standalone.subtitle", "Otvoreno u posebnom ekranu — drvo strukture ostaje u prvom.")
            : t("structure.subtitle", "Drvo pozicija — logička mesta na kojima se ugrađuje oprema.")}</div>
        </div>
        {standalone ? (
          <div className="page-actions">
            {selectedId && (
              <Link className="btn" to={`/struktura/${selectedId}`}>
                <Ic.tree /> {t("structure.openInStructure", "Otvori u strukturi")}
              </Link>
            )}
          </div>
        ) : (
        <div className="page-actions">
          <div className="chip-group" role="group" aria-label={t("structure.viewMode", "Prikaz")}>
            <button type="button" className={`chip${viewMode === "tree" ? " active" : ""}`} aria-pressed={viewMode === "tree"} onClick={() => setViewMode("tree")}>
              <Ic.tree /> {t("structure.view.tree", "Drvo")}
            </button>
            <button type="button" className={`chip${viewMode === "table" ? " active" : ""}`} aria-pressed={viewMode === "table"} onClick={() => setViewMode("table")}>
              <Ic.list /> {t("structure.view.table", "Tabela")}
            </button>
          </div>
          {canImport && (
            // With a position picked, the import offers it as the place where the machine goes.
            <Link className="btn" to={selectedId && detailInScope ? `/struktura/uvoz?parentId=${selectedId}` : "/struktura/uvoz"} style={{ textDecoration: "none" }}>
              <Ic.upload /> {t("structureImport.open", "Uvezi iz Excela")}
            </Link>
          )}
          {/* The first level is outside every branch of a structure scope. */}
          {canCreate && !scope.limited && (
            <button type="button" className="btn primary" onClick={() => setModal({ kind: "create", parent: null })}>
              <Ic.plus /> {t("structure.addTopLevel", "Nova pozicija na prvom nivou")}
            </button>
          )}
        </div>
        )}
      </div>

      {!standalone && tableMounted && (
        // Row click opens the position in the tree view (the card lives next to the tree).
        <div hidden={viewMode !== "table"}>
          <PositionsTable onOpen={(p) => navigate(`/pozicija/${p.id}`)} />
        </div>
      )}
      {/* Standalone card is never a "table" — the view-mode switch belongs to the tree page only. */}
      <div hidden={!standalone && viewMode !== "tree"}>
      {treeError && <div style={errorBox} role="alert">{treeError}</div>}

      <div className={standalone ? "" : "split-layout is-resizable"} style={standalone ? undefined : split.layoutStyle}>
        {!standalone && (<>
        <aside className="card split-side" style={{ padding: 8 }}>
          <div className="tree-tools" ref={treeToolsRef}>
            {!treeToolsCompact && (<>
              <button type="button" className="btn sm" onClick={() => { void expandAll(); }} disabled={expandingAll || roots === null} data-tip={t("structure.expandAllHint", "Otvori sve grane")}>
                <Ic.arrowDown /> {t("structure.expandAll", "Raširi sve")}
              </button>
              <button type="button" className="btn sm" onClick={collapseAll} disabled={roots === null || expanded.size === 0} data-tip={t("structure.collapseAllHint", "Zatvori sve grane")}>
                <Ic.arrowUp /> {t("structure.collapseAll", "Skupi sve")}
              </button>
            </>)}
            {/* Rarely used tree options live behind "⋯" so they don't eat the tree's width. */}
            <button
              type="button"
              className="btn sm tree-tools-more"
              aria-label={t("structure.treeOptions", "Opcije drveta")}
              data-tip={t("structure.treeOptions", "Opcije drveta")}
              aria-haspopup="menu"
              onClick={(e) => { const r = e.currentTarget.getBoundingClientRect(); setTreeMenu({ x: r.left, y: r.bottom + 4 }); }}
            >
              <Ic.more />
            </button>
          </div>
          {showDeleted && (
            <div className="tree-focus">
              <span>{t("structure.deletedShown", "Prikazane i obrisane")}</span>
              <button type="button" className="btn sm" style={{ marginLeft: "auto" }} onClick={() => setShowDeleted(false)}>
                <Ic.x /> {t("structure.hideDeleted", "Sakrij")}
              </button>
            </div>
          )}
          {treeRoot && (
            <div className="tree-focus">
              <button type="button" className="btn sm" onClick={() => setTreeRoot(null)}>
                <Ic.arrowUp /> {t("structure.focus.all", "Cela struktura")}
              </button>
              <span>{t("structure.focus.branch", "Grana:")}</span>
              <b>{treeRoot.name}</b>
            </div>
          )}
          {roots === null && !treeError ? (
            <p className="muted" style={{ padding: 12, margin: 0 }}>{t("common.loading", "Učitavanje…")}</p>
          ) : (
            <TreeView<PositionNode>
              nodes={roots ?? []}
              getId={getId}
              getLabel={getLabel}
              getChildren={getChildren}
              getHasChildren={getHasChildren}
              onExpand={onExpand}
              selectedId={selectedId}
              onSelect={(n) => select(n.id)}
              renderIcon={(n) => <PositionKindIcon kind={n.kind} />}
              renderBadge={renderBadge}
              onContextMenu={onRowContextMenu}
              expandedIds={expanded}
              onExpandedChange={setExpanded}
              ariaLabel={t("structure.title", "Struktura")}
              emptyText={canCreate && !scope.limited
                ? t("structure.emptyManage", "Struktura je prazna — dodajte prvu poziciju na prvom nivou.")
                : t("structure.empty", "Struktura je prazna.")}
              expandLabel={t("tree.expand", "Proširi")}
              collapseLabel={t("tree.collapse", "Skupi")}
              loadingLabel={t("common.loading", "Učitavanje…")}
            />
          )}
        </aside>
        <SplitHandle {...split.handleProps} />
        </>)}

        <div style={{ display: "flex", flexDirection: "column", gap: 16, minWidth: 0, opacity: detailLoading && detail ? 0.6 : 1, transition: "opacity .15s" }} aria-busy={detailLoading}>
          {!selectedId ? (
            <section className="card">
              <div className="empty">{t("structure.noneSelected", "Izaberite poziciju u drvetu da vidite karton.")}</div>
            </section>
          ) : detailError ? (
            <section className="card"><div style={errorBox} role="alert">{detailError}</div></section>
          ) : !detail ? (
            <section className="card"><div className="empty">{t("common.loading", "Učitavanje…")}</div></section>
          ) : (
            <>
              <DeletedBanner detail={detail} canRestore={canDelete && detailInScope} onRestore={() => { void handleRestore(); }} />
              {/* Header: name, code, kind chip, breadcrumb, actions */}
              <section className="card">
                <div className="card-h" style={{ alignItems: "flex-start" }}>
                  <div style={{ minWidth: 0 }}>
                    <nav className="crumbs" aria-label={t("structure.field.path", "Putanja")}>
                      {detail.path.map((p) => (
                        <span key={p.id}>
                          <button type="button" className="crumb-btn" onClick={() => select(p.id)}>{p.name}</button>
                        </span>
                      ))}
                      <span>{positionTitle(detail)}</span>
                    </nav>
                    <h3 style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 17 }}>
                      <PositionKindIcon kind={detail.kind} />
                      <span>{detail.name}{detail.sequenceNo !== null && <span className="muted"> #{detail.sequenceNo}</span>}</span>
                    </h3>
                    <div className="sub" style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", marginTop: 6 }}>
                      <span className="tag"><PositionKindName kind={detail.kind} /></span>
                      {detail.code && <span style={{ fontFamily: "var(--mono)" }}>{detail.code}</span>}
                      {detail.deletedAt && <span className="tag err dot">{t("structure.deletedShort", "Obrisana")}</span>}
                      {detail.mountState === "EMPTY" && !detail.deletedAt && <span className="tag err dot">{t("structure.emptySlotShort", "Prazna")}</span>}
                      {detail.mountState === "MOUNTED" && <span className="tag ok dot">{t("structure.mountedShort", "Montiran")}</span>}
                      {detail.mountState === "TEMPORARY" && <span className="tag warn dot">{t("structure.temporaryShort", "Privremena zamena")}</span>}
                      {detail.descendantCount > 0 && (
                        <span className="muted">{t("structure.descendants", "{{count}} pozicija u grani", { count: detail.descendantCount })}</span>
                      )}
                    </div>
                  </div>
                  {!standalone && (
                    <button type="button" className="icon-btn" title={t("structure.openInNewScreen", "Otvori u novom ekranu")} aria-label={t("structure.openInNewScreen", "Otvori u novom ekranu")} onClick={() => openInNewScreen(detail.id)}>
                      <Ic.external />
                    </button>
                  )}
                </div>
                {!detail.deletedAt && (canCreate || canEdit || canDelete || canCreateTemplate) && (
                  <div className="card-b" style={{ paddingTop: 12, paddingBottom: 12 }}>
                    <div className="form-actions" style={{ justifyContent: "flex-start" }}>
                      {canCreate && detailInScope && (
                        <button type="button" className="btn primary sm" onClick={() => setModal({ kind: "create", parent: detail })}>
                          <Ic.plus /> {t("structure.addChild", "Dodaj pod-poziciju")}
                        </button>
                      )}
                      {canEdit && detailInScope && (
                        <button type="button" className="btn sm" onClick={() => setModal({ kind: "edit", position: detail })}>
                          {t("action.edit", "Izmeni")}
                        </button>
                      )}
                      {detail.childCount > 0 && treeRoot?.id !== detail.id && (
                        <button type="button" className="btn sm" onClick={() => setTreeRoot({ id: detail.id, name: positionTitle(detail), chain: [...detail.path.map((p) => p.id), detail.id] })}>
                          <Ic.tree /> {t("structure.focus.here", "Fokus na granu")}
                        </button>
                      )}
                      {(canCreate || (canEdit && detailInScope) || canCreateTemplate) && (
                        <button
                          type="button"
                          className="btn sm"
                          aria-label={t("structure.moreActions", "Više akcija")}
                          data-tip={t("structure.moreActions", "Više akcija")}
                          aria-haspopup="menu"
                          onClick={(e) => { const r = e.currentTarget.getBoundingClientRect(); setMoreMenu({ x: r.left, y: r.bottom + 4 }); }}
                        >
                          <Ic.more />
                        </button>
                      )}
                      {canDelete && detailInScope && (
                        <button type="button" className="btn sm" style={dangerBtn} onClick={() => handleDelete()}>
                          {t("action.delete", "Obriši")}
                        </button>
                      )}
                    </div>
                    {outOfScopeNote && (
                      <p className="muted" style={{ margin: "10px 0 0", fontSize: 12.5 }}>
                        {t("structure.outOfScope", "Ova pozicija je van vašeg opsega izmena strukture — ovde možete samo da gledate, kopirate granu na svoje mesto ili sačuvate šablon.")}
                      </p>
                    )}
                  </div>
                )}
              </section>

              {/* Expected type + reference values: only kinds that hold an asset (structural nodes have neither) */}
              {holdsAssetOf(detail.kind) && (
                <ReferenceValuesSection detail={detail} canEdit={canEdit && detailInScope} onSaved={setDetail} />
              )}

              <LocationSection detail={detail} />

              {/* Drawing + attachments (DRAWING listed first — contract §6) */}
              <AttachmentsPanel
                entityKind="POSITION"
                entityId={detail.id}
                entityCode={detail.code}
                title={t("structure.section.attachments", "Crtež i prilozi")}
              />

              <NotesSection detail={detail} />

              <MountedAssetSection
                detail={detail}
                holdsAsset={holdsAssetOf(detail.kind)}
                canMount={canMount}
                canViewAssets={canViewAssets}
                onMount={() => setModal({ kind: "mount", position: detail })}
                onDismount={() => setModal({ kind: "dismount", position: detail })}
                onDismountAll={() => { void openDismountAll(detail); }}
              />

              {/* Sastavnica (sprint 5l): articles with quantities — every kind of position, below the mounted asset (2026-10-12) */}
              <BomSection
                detail={detail}
                canCreate={canCreateBom && detailInScope}
                canEdit={canEditBom && detailInScope}
                canDelete={canDeleteBom && detailInScope}
                onChanged={() => setHistoryKey((k) => k + 1)}
              />

              {canViewLayouts && (
                <PositionLayoutsSection
                  detail={detail}
                  canCreateLayout={canCreateLayout}
                  onNewLayout={() => setModal({ kind: "newLayout", position: detail })}
                />
              )}

              {/* Position history (MOUNT / DISMOUNT with this position) */}
              {canViewAssets && (
                <section className="card">
                  <div className="card-h">
                    <div>
                      <h3>{t("structure.section.history", "Istorija pozicije")}</h3>
                      <div className="sub">{t("structure.historySub", "Šta je sve bilo montirano na ovom mestu.")}</div>
                    </div>
                  </div>
                  <AssetEventsTable load={loadPositionEvents} refreshKey={eventsKey} showAsset emptyText={t("structure.historyEmpty", "Na ovoj poziciji još nije bilo montaže.")} />
                </section>
              )}

              {/* Free-text notes (sprint 4) */}
              <CommentsPanel entityKind="POSITION" entityId={detail.id} />

              {/* Who changed what on this position (sprint 5d) */}
              <HistoryPanel entityKind="POSITION" entityId={detail.id} title={t("structure.changeHistory", "Istorija izmena")} refreshKey={historyKey} />
            </>
          )}
        </div>
      </div>
      </div>

      {ctx && ctxItems.length > 0 && <ContextMenu x={ctx.x} y={ctx.y} items={ctxItems} onClose={closeCtx} />}
      {treeMenu && (
        <ContextMenu
          x={treeMenu.x}
          y={treeMenu.y}
          onClose={() => setTreeMenu(null)}
          items={[
            ...(treeToolsCompact ? [
              { label: t("structure.expandAll", "Raširi sve"), icon: <Ic.arrowDown />, onSelect: () => { void expandAll(); } },
              { label: t("structure.collapseAll", "Skupi sve"), icon: <Ic.arrowUp />, onSelect: collapseAll },
            ] : []),
            {
              label: showDeleted
                ? t("structure.hideDeletedFull", "Sakrij obrisane")
                : t("structure.showDeleted", "Prikaži obrisane"),
              icon: showDeleted ? <Ic.eyeOff /> : <Ic.eye />,
              separator: treeToolsCompact,
              onSelect: () => setShowDeleted((v) => !v),
            },
            {
              label: showSlotCounts
                ? t("structure.hideSlotCounts", "Sakrij broj praznih pozicija")
                : t("structure.showSlotCounts", "Prikaži broj praznih pozicija"),
              icon: showSlotCounts ? <Ic.eyeOff /> : <Ic.eye />,
              onSelect: () => setShowSlotCounts((v) => !v),
            },
          ]}
        />
      )}
      {moreMenu && detail && (
        <ContextMenu
          x={moreMenu.x}
          y={moreMenu.y}
          onClose={() => setMoreMenu(null)}
          items={[
            ...(canEdit && detailInScope ? [{ label: t("structure.move", "Premesti"), icon: <Ic.arrowRight />, onSelect: () => setModal({ kind: "move", position: detail }) }] : []),
            ...(canCreate ? [{ label: t("structure.copy", "Kopiraj granu"), icon: <Ic.copy />, onSelect: () => setModal({ kind: "copy", position: detail }) }] : []),
            ...(canCreateTemplate ? [{ label: t("structure.saveAsTemplate", "Sačuvaj kao šablon"), icon: <Ic.template />, separator: (canEdit && detailInScope) || canCreate, onSelect: () => setModal({ kind: "saveTemplate", position: detail }) }] : []),
            ...(canCreate && canViewTemplates && detailInScope ? [{ label: t("structure.addFromTemplate", "Dodaj iz šablona"), icon: <Ic.layers />, onSelect: () => setModal({ kind: "applyTemplate", position: detail }) }] : []),
          ]}
        />
      )}

      {modal?.kind === "create" && (
        <PositionFormModal
          mode={modal}
          onClose={() => setModal(null)}
          onCreated={(created) => handleCreated(modal.parent, created)}
          onEdited={handleEdited}
        />
      )}
      {modal?.kind === "edit" && (
        <PositionFormModal
          mode={modal}
          onClose={() => setModal(null)}
          onCreated={() => Promise.resolve()}
          onEdited={handleEdited}
        />
      )}
      {modal?.kind === "move" && (
        <MoveModal position={modal.position} onClose={() => setModal(null)} onMoved={handleMoved} />
      )}
      {modal?.kind === "copy" && (
        <CopyModal position={modal.position} onClose={() => setModal(null)} onCopied={handleCopied} />
      )}
      {modal?.kind === "mount" && (
        <MountModal position={modal.position} onClose={() => setModal(null)} onMounted={(d) => { void handleMounted(d); }} />
      )}
      {modal?.kind === "dismount" && modal.position.mountedAsset && (
        <DismountModal asset={modal.position.mountedAsset} onClose={() => setModal(null)} onDone={(d) => { void handleDismounted(d); }} />
      )}
      {modal?.kind === "dismountAll" && (
        <DismountModal position={modal.position} count={modal.count} onClose={() => setModal(null)} onDone={(r) => { void handleDismountedAll(r); }} />
      )}
      {modal?.kind === "saveTemplate" && (
        <SaveTemplateModal position={modal.position} onClose={() => setModal(null)} onSaved={handleTemplateSaved} />
      )}
      {modal?.kind === "applyTemplate" && (
        <ApplyTemplateModal position={modal.position} onClose={() => setModal(null)} onApplied={(created) => handleTemplateApplied(modal.position, created)} />
      )}
      {modal?.kind === "newLayout" && (
        <NewLayoutModal
          position={{ id: modal.position.id, name: modal.position.name, code: modal.position.code, sequenceNo: modal.position.sequenceNo }}
          onClose={() => setModal(null)}
          onCreated={(d) => { setModal(null); navigate(`/layout/${d.id}?edit=1`); }}
        />
      )}
    </div>
  );
}
